/**
 * How an edge's content lies along it: its length, the corners that bound
 * each chamfer leg, and its segments in position order, each with its
 * neighbors. Internal to constraints/, apart from `isHorizontalEdge` and
 * `edgeLength`.
 */
import { edgeLeg, isHorizontal } from '../geometry';
import { type CornerName, type EdgeName, edgeSegments, type FloorPlanEdgeSegment, type FloorPlanRoom } from '../schemas';

/** @deprecated `isHorizontal` under its older name, kept until the editor core calls `isHorizontal`. */
export const isHorizontalEdge = isHorizontal;

export function edgeLength(shape: FloorPlanRoom, edge: EdgeName): number {
  return isHorizontal(edge) ? shape.width : shape.length;
}

/** Maps each corner's dx/dy leg to the (edge, role) that bounds it. */
export const CORNER_LEG_EDGE: Record<
  CornerName,
  { dx: [EdgeName, 'start' | 'end']; dy: [EdgeName, 'start' | 'end'] }
> = {
  backLeftCorner: { dx: ['backEdge', 'start'], dy: ['leftEdge', 'end'] },
  backRightCorner: { dx: ['backEdge', 'end'], dy: ['rightEdge', 'start'] },
  frontRightCorner: { dx: ['frontEdge', 'start'], dy: ['rightEdge', 'end'] },
  frontLeftCorner: { dx: ['frontEdge', 'end'], dy: ['leftEdge', 'start'] },
};

/** A segment on a primary edge, with its index in the edge's stored list. */
export interface StoredSegment {
  segment: FloorPlanEdgeSegment;
  storageIndex: number;
}

/** The edge's segments in position order. */
export function segmentsInPositionOrder(shape: FloorPlanRoom, edge: EdgeName): StoredSegment[] {
  return edgeSegments(shape, edge)
    .map((segment, storageIndex) => ({ segment, storageIndex }))
    .sort((first, second) => first.segment.position - second.segment.position);
}

/** Where the last segment along the edge ends; 0 when the edge has none. */
export function lastSegmentEnd(shape: FloorPlanRoom, edge: EdgeName): number {
  const sorted = segmentsInPositionOrder(shape, edge);
  const last = sorted[sorted.length - 1]?.segment;
  return last ? last.position + last.width : 0;
}

export interface AlcoveNeighbors {
  segment: FloorPlanEdgeSegment;
  /** Where the previous segment ends, or the start chamfer's inset when there is none. */
  previousEnd: number;
  /** Where the next segment starts, or the end chamfer's inset from the edge's end when there is none. */
  nextStart: number;
}

/** The segment stored at `storageIndex` and the span its neighbors leave it. Throws for an index the edge doesn't store. */
export function alcoveNeighbors(shape: FloorPlanRoom, edge: EdgeName, storageIndex: number): AlcoveNeighbors {
  const sorted = segmentsInPositionOrder(shape, edge);
  const index = sorted.findIndex(entry => entry.storageIndex === storageIndex);
  const { segment } = sorted[index];
  const previous = sorted[index - 1]?.segment;
  const next = sorted[index + 1]?.segment;
  return {
    segment,
    previousEnd: previous ? previous.position + previous.width : edgeLeg(shape, edge, true),
    nextStart: next ? next.position : edgeLength(shape, edge) - edgeLeg(shape, edge, false),
  };
}
